import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, Facebook, Newspaper, Lightbulb } from "lucide-react";
import { FACEBOOK_URL } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICON = { "Συμβουλές": Lightbulb, "Νέα": Newspaper };

export default function EduNews() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/edu-news`)
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  }, []);

  if (items.length === 0) return null;

  return (
    <section id="edu-news" data-testid="edu-news-section" className="py-20 md:py-32 bg-secondary/40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-4">// Εκπαιδευτικά Νέα</div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
              Ό,τι πρέπει να ξέρεις για την εκπαίδευση.
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Νέα από τη δευτεροβάθμια εκπαίδευση — μαθήματα, προαγωγικές εξετάσεις, Πανελλήνιες — και άρθρα με χρήσιμες συμβουλές.
            </p>
          </div>
          <a
            data-testid="edu-news-facebook"
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 shrink-0 bg-[#1877F2] text-white font-mono-accent text-xs uppercase tracking-widest px-6 py-3.5 rounded-sm hover:opacity-90 transition-opacity"
          >
            <Facebook className="h-4 w-4" />
            Ακολουθήστε μας στο Facebook
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((n, i) => {
            const Icon = CATEGORY_ICON[n.category] || Newspaper;
            const CardTag = n.url ? "a" : "div";
            const linkProps = n.url
              ? { href: n.url, target: "_blank", rel: "noopener noreferrer" }
              : {};
            return (
              <motion.div
                key={n.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
              >
                <CardTag
                  {...linkProps}
                  data-testid={`edu-news-item-${i}`}
                  className="group flex flex-col h-full bg-white border border-border rounded-sm p-6 hover:border-primary transition-colors"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-flex items-center gap-1.5 font-mono-accent text-[10px] uppercase tracking-widest bg-primary/10 text-primary px-2.5 py-1 rounded-sm">
                      <Icon className="h-3 w-3" />
                      {n.category}
                    </span>
                    {n.url && (
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    )}
                  </div>
                  <h3 className="font-heading font-bold text-lg leading-snug group-hover:text-primary transition-colors">
                    {n.title}
                  </h3>
                  {n.summary && (
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed flex-1">{n.summary}</p>
                  )}
                  {n.source && (
                    <div className="mt-5 pt-4 border-t border-border font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">
                      Πηγή: {n.source}
                    </div>
                  )}
                </CardTag>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
